<form
  class="flex h-full min-h-0 flex-col flex-1 gap-2"
  data-test-id="user-form"
  [formGroup]="form"
  (submit)="submitForm()"
>
  <div class="flex flex-1 min-h-0 flex-col gap-2 overflow-y-auto py-2">
    <div class="grid grid-cols-1 grid-rows-3 gt-sm:grid-cols-2 gap-2 justify-stretch">
      <mat-form-field class="flex-1">
        <mat-label data-test-id="user-form-first-name-label">First name</mat-label>
        <input
          type="text"
          matInput
          placeholder="First name"
          formControlName="firstName"
          data-test-id="user-form-first-name"
        />
        <mat-error data-test-id="user-form-first-name-error"> Required </mat-error>
      </mat-form-field>

      <mat-form-field class="flex-1">
        <mat-label data-test-id="user-form-last-name-label">Last name</mat-label>
        <input
          type="text"
          matInput
          placeholder="Last name"
          formControlName="lastName"
          data-test-id="user-form-last-name"
        />
        <mat-error data-test-id="user-form-last-name-error"> Required </mat-error>
      </mat-form-field>

      <mat-form-field class="flex-1 col-span-1 gt-sm:col-span-2">
        <mat-label data-test-id="user-form-email-label">Email address</mat-label>
        <input
          type="text"
          matInput
          placeholder="Email address"
          formControlName="email"
          data-test-id="user-form-email"
        />
        <mat-error data-test-id="user-form-email-error">
          @if (form.controls.email.errors?.email) {
            Enter a valid email
          }
          @if (form.controls.email.errors?.required) {
            Required
          }
        </mat-error>
      </mat-form-field>

      <mat-form-field class="flex-1">
        <mat-label data-test-id="user-form-country-label">Country</mat-label>
        <mat-select formControlName="country" data-test-id="user-form-country">
          @for (country of countries; track country) {
            <mat-option data-test-id="user-form-country-option" [value]="country.abbreviation">
              {{ country.country }}
            </mat-option>
          }
        </mat-select>
        <mat-error data-test-id="user-form-country-error"> Required </mat-error>
      </mat-form-field>

      <mat-form-field class="flex-1">
        <mat-label data-test-id="user-form-timezone-label">Timezone</mat-label>
        <mat-select formControlName="timezone" data-test-id="user-form-timezone">
          @for (timezone of timezones; track timezone) {
            <mat-option data-test-id="user-form-timezone-option" [value]="timezone.name">
              {{ timezone.offset }} {{ timezone.abbr }} {{ timezone.name }}
            </mat-option>
          }
        </mat-select>
        <mat-error data-test-id="user-form-timezone-error"> Required </mat-error>
      </mat-form-field>

      @if (clientIdsControl) {
        <mat-form-field class="flex-1 col-span-2">
          <mat-label data-test-id="user-form-clients-label">Clients</mat-label>
          <mat-select formControlName="clientIds" multiple data-test-id="user-form-clients">
            @for (client of clients; track client.clientId) {
              <mat-option data-test-id="user-form-clients-option" [value]="client.clientId">
                {{ client.clientName }}
              </mat-option>
            }
          </mat-select>
          <mat-error data-test-id="user-form-clients-error"> Required </mat-error>
        </mat-form-field>
      }
    </div>

    <div class="grid grid-cols-1 grid-rows-2 gt-sm:grid-cols-2 gap-2 justify-stretch">
      @if (jobTitleControl) {
        <mat-form-field class="flex-1">
          <mat-label data-test-id="user-form-job-title-label">Job title</mat-label>
          <mat-select placeholder="Job title" formControlName="job_title" data-test-id="user-form-job-title">
            @for (job of jobTitles; track job) {
              <mat-option data-test-id="user-form-job-title-option" [value]="job">
                {{ job | jobTitle }}
              </mat-option>
            }
          </mat-select>
          <mat-error data-test-id="user-form-job-title-error"> Required </mat-error>
        </mat-form-field>
      }

      @if (phoneNumberControl) {
        <mat-form-field class="flex-1">
          <mat-label data-test-id="user-form-phone-number-label">Phone number</mat-label>
          <input
            type="text"
            matInput
            placeholder="Phone number"
            formControlName="phone_number"
            data-test-id="user-form-phone-number"
          />
        </mat-form-field>
      }

      @if (officeLocationControl) {
        <mat-form-field class="flex-1">
          <mat-label data-test-id="user-form-office-location-label">Office location</mat-label>
          <mat-select
            placeholder="Office location"
            formControlName="office_location"
            data-test-id="user-form-office-location"
          >
            @for (location of officeLocations; track location) {
              <mat-option data-test-id="user-form-office-location-option" [value]="location">
                {{ location | titlecase }}
              </mat-option>
            }
          </mat-select>
          <mat-error data-test-id="user-form-office-location-error"> Required </mat-error>
        </mat-form-field>
      }

      @if (linkedInURLControl) {
        <mat-form-field class="flex-1">
          <mat-label data-test-id="user-form-linkedin-label">LinkedIn URL</mat-label>
          <input
            type="text"
            matInput
            placeholder="LinkedIn URL"
            formControlName="linkedin_url"
            data-test-id="user-form-linkedin"
          />
        </mat-form-field>
      }

      @if (microsoftIdControl) {
        <mat-form-field class="flex-1">
          <mat-label data-test-id="user-form-microsoft-id-label">Microsoft ID</mat-label>
          <input
            type="text"
            matInput
            placeholder="Microsoft ID"
            formControlName="microsoft_id"
            data-test-id="user-form-microsoft-id"
          />
        </mat-form-field>
      }
    </div>
  </div>

  <div class="mt-auto flex w-full flex-none justify-end gap-2 pt-2">
    @if (user && canDeactivateUser) {
      <button
        matButton="outlined"
        color="warn"
        type="button"
        data-test-id="user-form-deactivate"
        [disabled]="!user.is_active || isSubmitting"
        [matTooltip]="user.is_active ? '' : 'User inactive'"
        (click)="deactivateUser()"
      >
        Deactivate
      </button>
    }
    <button
      matButton="filled"
      color="primary"
      type="submit"
      data-test-id="user-form-submit"
      [disabled]="(user && !user.is_active) || isSubmitting"
    >
      Submit
    </button>
  </div>
</form>
